/** Founder's View telemetry. Isolated from client messaging. */
import { HUB_CATEGORIES, THEHUB_PREFIX, categoryLabel } from "./constants.js";

const KEYS = {
  visits: `${THEHUB_PREFIX}visits`,
  stats: `${THEHUB_PREFIX}event_stats`,
  rooms: `${THEHUB_PREFIX}rooms`,
  warnings: `${THEHUB_PREFIX}warnings`,
  settings: `${THEHUB_PREFIX}founder_settings`,
  night: `${THEHUB_PREFIX}founder_night`,
};

const FLOWS = [
  ["city", "county"],
  ["county", "state"],
  ["state", "national"],
  ["associations", "chambers"],
  ["chambers", "gatherings"],
];

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
  try {
    localStorage.setItem(`${THEHUB_PREFIX}revision`, String(Date.now()));
    window.dispatchEvent(new Event("dh-thehub-change"));
  } catch {
    /* ignore */
  }
}

function hoursAgo(n) {
  return new Date(Date.now() - n * 3600 * 1000).toISOString();
}

function seedVisits() {
  const rows = [];
  FLOWS.forEach(([from, to], index) => {
    const count = 4 + index * 2;
    for (let i = 0; i < count; i += 1) {
      rows.push({
        id: `fv-${from}-${to}-${i}`,
        user_id: i % 2 ? "agent-demo" : "broker-demo",
        from,
        to,
        at: hoursAgo(6 + i * 5 + index),
      });
    }
  });
  ["chat", "city", "chambers", "gatherings"].forEach((room, index) => {
    for (let i = 0; i < 8; i += 1) {
      rows.push({
        id: `fv-stay-${room}-${i}`,
        user_id: "agent-demo",
        from: room,
        to: room,
        at: hoursAgo(i * 3 + index),
      });
    }
  });
  return rows;
}

export function ensureFounderHub() {
  if (!Array.isArray(read(KEYS.visits, null))) write(KEYS.visits, seedVisits());
  if (read(KEYS.stats, null) == null) write(KEYS.stats, {});
  if (!Array.isArray(read(KEYS.rooms, null))) write(KEYS.rooms, []);
  if (!Array.isArray(read(KEYS.warnings, null))) write(KEYS.warnings, []);
  if (read(KEYS.settings, null) == null) {
    write(KEYS.settings, { intervalHours: 24, offSources: [] });
  }
  return true;
}

export function recordHubVisit(userId, to, from = "") {
  if (!to || to === "hall") return;
  ensureFounderHub();
  const rows = read(KEYS.visits, []);
  rows.push({
    id: crypto.randomUUID(),
    user_id: userId || "guest",
    from: from && from !== to ? from : "",
    to,
    at: new Date().toISOString(),
  });
  write(KEYS.visits, rows.slice(-800));
}

export function listHubVisits() {
  ensureFounderHub();
  return read(KEYS.visits, []);
}

export function bumpEventStat(eventId, field) {
  if (!eventId) return;
  ensureFounderHub();
  const stats = read(KEYS.stats, {});
  const current = stats[eventId] || { views: 0, interested: 0, attending: 0, shares: 0 };
  current[field] = Number(current[field] || 0) + 1;
  stats[eventId] = current;
  write(KEYS.stats, stats);
}

export function listEventStats() {
  ensureFounderHub();
  return read(KEYS.stats, {});
}

export function listRoomOverlay() {
  ensureFounderHub();
  return read(KEYS.rooms, []);
}

function overlayFor(id) {
  return listRoomOverlay().find((row) => row.id === id) || {};
}

export function listAllHubRooms() {
  ensureFounderHub();
  const overlay = listRoomOverlay();
  const custom = overlay.filter((row) => row.custom && !row.removed);
  const built = HUB_CATEGORIES.map((room) => {
    const extra = overlayFor(room.id);
    return {
      ...room,
      label: extra.label || room.label,
      blurb: extra.blurb || room.blurb,
      muted: Boolean(extra.muted),
      boosted: Boolean(extra.boosted),
      featured: Boolean(extra.featured),
      archived: Boolean(extra.archived),
      custom: false,
    };
  });
  return [
    ...built,
    ...custom.map((row) => ({
      id: row.id,
      label: row.label,
      blurb: row.blurb || "A custom room.",
      kind: "custom",
      initials: (row.label || "RM").slice(0, 2).toUpperCase(),
      tone: { wash: "#ece8e2", ink: "#2c2c2c", accent: "#6a5a44" },
      muted: Boolean(row.muted),
      boosted: Boolean(row.boosted),
      featured: Boolean(row.featured),
      archived: Boolean(row.archived),
      custom: true,
    })),
  ];
}

export function listVisibleHubRooms() {
  return listAllHubRooms()
    .filter((row) => !row.archived)
    .sort((a, b) => Number(b.featured) - Number(a.featured) || Number(b.boosted) - Number(a.boosted));
}

export function isKnownHubRoom(id) {
  return listAllHubRooms().some((row) => row.id === id);
}

export function roomMeta(id) {
  return listAllHubRooms().find((row) => row.id === id) || null;
}

export function patchHubRoom(id, patch) {
  ensureFounderHub();
  const rows = listRoomOverlay();
  const index = rows.findIndex((row) => row.id === id);
  const next = { ...(index === -1 ? { id } : rows[index]), ...patch, id };
  if (index === -1) rows.push(next);
  else rows[index] = next;
  write(KEYS.rooms, rows);
  return next;
}

export function addCustomHubRoom({ label, blurb }) {
  const name = String(label || "").trim();
  if (!name) throw new Error("Give the room a name.");
  const id = `custom-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}-${Date.now().toString(36)}`;
  return patchHubRoom(id, { custom: true, label: name, blurb: String(blurb || "").trim(), archived: false });
}

export function removeHubRoom(id) {
  const room = listAllHubRooms().find((row) => row.id === id);
  if (!room) throw new Error("Room not found.");
  if (room.custom) return patchHubRoom(id, { removed: true, archived: true });
  return patchHubRoom(id, { archived: true });
}

export function listHubWarnings() {
  ensureFounderHub();
  return read(KEYS.warnings, []);
}

export function warnHubMember(userId, note) {
  if (!userId) throw new Error("A member id is needed.");
  const rows = listHubWarnings();
  rows.push({ user_id: userId, note: String(note || "Please keep The Hub unhurried."), at: new Date().toISOString() });
  write(KEYS.warnings, rows);
}

export function getFounderSettings() {
  ensureFounderHub();
  return { intervalHours: 24, offSources: [], ...read(KEYS.settings, {}) };
}

export function setFounderSettings(patch) {
  const next = { ...getFounderSettings(), ...patch };
  write(KEYS.settings, next);
  return next;
}

export function isFounderNight() {
  return read(KEYS.night, true) === true;
}

export function setFounderNight(on) {
  write(KEYS.night, Boolean(on));
}

export function heatLevel(score) {
  if (score >= 10) return { id: "surging", label: "Surging", tone: "red" };
  if (score >= 6) return { id: "trending", label: "Trending", tone: "orange" };
  if (score >= 3) return { id: "warming", label: "Warming", tone: "yellow" };
  if (score >= 1) return { id: "healthy", label: "Healthy", tone: "green" };
  return { id: "inactive", label: "Inactive", tone: "gray" };
}

function dayKey(iso) {
  return String(iso || "").slice(0, 10);
}

function hourOf(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 0 : date.getHours();
}

const POSITIVE = /\b(thank|thanks|helpful|yes|attending|glad|useful|kind)\b/i;
const FRICTION = /\?|still happening|where is|when is|confused|wrong|incorrect/i;

export function classifyEngagement(text) {
  const body = String(text || "");
  if (FRICTION.test(body)) return "friction";
  if (POSITIVE.test(body)) return "positive";
  return "neutral";
}

export function buildFounderSnapshot({ posts = [], events = [], requests = [], groups = [] } = {}) {
  ensureFounderHub();
  const visits = listHubVisits();
  const stats = listEventStats();
  const rooms = listAllHubRooms();
  const dayAgo = Date.now() - 24 * 3600 * 1000;
  const recentPosts = posts.filter((row) => Date.parse(row.created_at) > dayAgo);
  const recentVisits = visits.filter((row) => Date.parse(row.at) > dayAgo);

  const heatmap = rooms
    .filter((row) => !row.archived)
    .map((room) => {
      const score =
        recentPosts.filter((row) => row.category === room.id).length +
        recentVisits.filter((row) => row.to === room.id).length;
      return { ...room, score, heat: heatLevel(score) };
    });

  const feed = [
    ...posts.slice(-12).reverse().map((row) => ({
      id: `p-${row.id}`,
      at: row.created_at,
      kind: row.parent_id ? "reply" : row.kind === "announcement" ? "announcement" : "post",
      text: `${row.author_name} · ${categoryLabel(row.category)} · ${String(row.body || "").slice(0, 80)}`,
    })),
    ...requests.slice(0, 8).map((row) => ({
      id: `r-${row.id}`,
      at: row.created_at,
      kind: row.status === "approved" ? "approval" : "request",
      text: `${row.from_name || "Member"} · ${row.mode} · ${row.title}`,
    })),
    ...groups.slice(0, 4).map((row) => ({
      id: `g-${row.id}`,
      at: row.created_at,
      kind: "association",
      text: `Circle · ${row.name}`,
    })),
  ]
    .sort((a, b) => String(b.at).localeCompare(String(a.at)))
    .slice(0, 16);

  const attendingWords = /\b(attend|i'll be there|i will|count me in|yes)\b/i;
  const interestWords = /\b(interested|thinking about it|maybe)\b/i;
  const interest = events.slice(0, 8).map((event) => {
    const extra = stats[event.id] || {};
    const replies = posts.filter((row) => attendingWords.test(row.body) && row.category === event.category);
    const curious = posts.filter((row) => interestWords.test(row.body) && row.category === event.category);
    return {
      id: event.id,
      title: event.title,
      views: Number(extra.views || 0),
      shares: Number(extra.shares || 0),
      attending: Number(extra.attending || 0) || replies.length,
      interested: Number(extra.interested || 0) || curious.length,
    };
  });

  const movement = FLOWS.map(([from, to]) => ({
    from,
    to,
    fromLabel: categoryLabel(from),
    toLabel: categoryLabel(to),
    count: visits.filter((row) => row.from === from && row.to === to).length,
  }));

  const hourly = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    count: [...recentPosts, ...recentVisits.map((row) => ({ created_at: row.at }))].filter(
      (row) => hourOf(row.created_at || row.at) === hour
    ).length,
  }));

  const weekly = Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - index));
    const key = date.toISOString().slice(0, 10);
    return {
      key,
      label: date.toLocaleDateString(undefined, { weekday: "short" }),
      count:
        posts.filter((row) => dayKey(row.created_at) === key).length +
        visits.filter((row) => dayKey(row.at) === key).length,
    };
  });

  const sentiment = { positive: 0, neutral: 0, friction: 0 };
  posts.forEach((row) => {
    sentiment[classifyEngagement(row.body)] += 1;
  });

  const authors = [...new Set(posts.map((row) => row.author_id).filter(Boolean))];

  return { heatmap, feed, interest, movement, hourly, weekly, sentiment, authors, rooms };
}
